// src/model/PawnMoveStrategy.ts
import { MoveStrategy } from './MoveStrategy';
import { Position, BoardState } from '../model/BoardState';

export class PawnMoveStrategy implements MoveStrategy {
  /**
   * Checks if the given position is within the bounds of the chess board.
   *
   * @param position - The position to check.
   * @returns `true` if the position is within bounds, `false` otherwise.
   */
  private isWithinBounds(position: Position): boolean {
    return position.row >= 0 && position.row < 8 && position.col >= 0 && position.col < 8;
  }
  
  getLegalMoves(position: Position, boardState: BoardState): Position[] {
    const moves: Position[] = [];
    const { row, col } = position;
    const direction = boardState.pieces.find(p => p.position.row === row && p.position.col === col)?.color === 'white' ? 1 : -1;

    // Move forward
    const forwardMove = { row: row + direction, col };
    if (this.isWithinBounds(forwardMove) && !this.isBlocked(forwardMove, boardState)) {
      moves.push(forwardMove);
    }

    // Capture diagonally
    const captureMoveRight = { row: row + direction, col: col + 1 };
    if (this.isWithinBounds(captureMoveRight) && this.isOpponentPiece(captureMoveRight, boardState, position)) {
      if (!moves.some(move => move.row === row + direction && move.col === col + 1)) {
        moves.push(captureMoveRight);
      }
    }
    
    const captureMoveLeft = { row: row + direction, col: col - 1 };
    if (this.isWithinBounds(captureMoveLeft) && this.isOpponentPiece(captureMoveLeft, boardState, position)) {
      if (!moves.some(move => move.row === row + direction && move.col === col - 1)) {
        moves.push(captureMoveLeft);
      }
    }

    // Initial double move
    if ((row === 1 && direction === 1) || (row === 6 && direction === -1)) {
      const doubleMovePosition = { row: row + 2 * direction, col };
      if (this.isWithinBounds(doubleMovePosition) && 
          !this.isBlocked(forwardMove, boardState) && 
          !this.isBlocked(doubleMovePosition, boardState)) {
        moves.push(doubleMovePosition);
      }
    }

    return moves;
  }

  getStrategyName(): string {
    return "Pawn Move Strategy";
  }

  private isBlocked(position: Position, boardState: BoardState): boolean {
    return boardState.pieces.some(p => p.position.row === position.row && p.position.col === position.col);
  }

  private isOpponentPiece(position: Position, boardState: BoardState, currentPosition: Position): boolean {
    const piece = boardState.pieces.find(p => p.position.row === position.row && p.position.col === position.col);
    const currentPiece = boardState.pieces.find(p => p.position.row === currentPosition.row && p.position.col === currentPosition.col);
    return piece ? piece.color !== currentPiece?.color : false;
  }
}

